import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useStore } from '../../stores/Store';
import { Button, SelectInput, TextInput, View } from '../../components';
import './style.scss';

const BANK_BRANCH_CODES = {
    'Absa Bank': '632005',
    'African Bank': '430000',
    'Bidvest Bank': '462005',
    'Capitec Bank': '470010',
    'Discovery Bank': '679000',
    'First National Bank': '250655',
    'Investec': '580105',
    'Nedbank': '198765',
    'Standard Bank': '051001',
    'TymeBank': '678910'
};

const Withdraw = observer(() => {
    const store = useStore();
    const { state } = useLocation();
    const navigate = useNavigate();
    const randAmount = Number(state?.randAmount || 0);
    const accountHolder = `${store.user.FirstName || ''} ${store.user.LastName || ''}`.trim();
    const [BankName, setBankName] = useState('');
    const [OtherBankName, setOtherBankName] = useState('');
    const [BranchCode, setBranchCode] = useState('');
    const [AccountNumber, setAccountNumber] = useState('');
    const [ProofOfAccount, setProofOfAccount] = useState('');
    const [ProofOfAccountFileName, setProofOfAccountFileName] = useState('');
    const [, setWithdrawalRequest] = useState(null);
    const [error, setError] = useState('');
    const proofOfAccountInputRef = useRef(null);

    const resolvedBankName = BankName === 'Other' ? OtherBankName.trim() : BankName;
    const isOtherBank = BankName === 'Other';
    const hasCompleteAccountDetails = Boolean(
        resolvedBankName &&
        BranchCode.trim() &&
        accountHolder &&
        AccountNumber.trim()
    );
    const canSubmit = Boolean(
        randAmount > 0 &&
        ProofOfAccount &&
        hasCompleteAccountDetails
    );

    const bankChanged = (event) => {
        const bankName = event.target.value;

        setBankName(bankName);
        setBranchCode(BANK_BRANCH_CODES[bankName] || '');
    };

    const proofOfAccountChanged = (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            setProofOfAccount('');
            setProofOfAccountFileName('');
            return;
        }

        if (file.type !== 'application/pdf') {
            event.target.value = '';
            setProofOfAccount('');
            setProofOfAccountFileName('');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result || '');
            setProofOfAccount(dataUrl.split(',')[1] || '');
            setProofOfAccountFileName(file.name);
        };
        reader.readAsDataURL(file);
    };

    const clearProofOfAccount = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setProofOfAccount('');
        setProofOfAccountFileName('');

        if (proofOfAccountInputRef.current) {
            proofOfAccountInputRef.current.value = '';
        }
    };

    const submitWithdrawal = async (event) => {
        event.preventDefault();
        setError('');

        if (!canSubmit) {
            return;
        }

        const withdrawalRequest = {
            IDNumber: store.user.IDNumber || '',
            Amount: randAmount,
            AccountDetails: {
                BankName: resolvedBankName,
                isOther: isOtherBank,
                BranchCode: BranchCode.trim(),
                AccountHolder: accountHolder,
                AccountNumber: AccountNumber.trim()
            },
            ProofOfAccount
        };

        setWithdrawalRequest(withdrawalRequest);
        store.setLoading(true);
        const result = await store.requestWithdrawal(withdrawalRequest);
        store.setLoading(false);

        if (result.success) {
            navigate('/withdrawsuccess');
        } else {
            setError(result.message);
        }
    };

    return (
        <View>
            <div className='withdrawPage'>
                <p style={{fontSize: '35px', marginTop: '0px', marginBottom: '40px', fontWeight: '900'}}>
                    <span className='welcomeUnderline'>Request</span> a Withdrawal
                </p>
                <form className='withdrawForm' onSubmit={submitWithdrawal}>
                    <TextInput
                        placeholder='Amount'
                        value={`R${randAmount.toFixed(2)}`}
                        onChange={() => {}}
                        className='withdrawReadOnlyInput'
                    />
                    <TextInput
                        placeholder='Account Holder'
                        value={accountHolder}
                        onChange={() => {}}
                        className='withdrawReadOnlyInput'
                    />
                    <SelectInput
                        placeholder='Bank Name'
                        value={BankName}
                        onChange={bankChanged}
                        id='withdrawBankName'
                    >
                        <option value='' disabled>Select a bank</option>
                        <option value='Absa Bank'>Absa Bank</option>
                        <option value='African Bank'>African Bank</option>
                        <option value='Bidvest Bank'>Bidvest Bank</option>
                        <option value='Capitec Bank'>Capitec Bank</option>
                        <option value='Discovery Bank'>Discovery Bank</option>
                        <option value='First National Bank'>First National Bank (FNB)</option>
                        <option value='Investec'>Investec</option>
                        <option value='Nedbank'>Nedbank</option>
                        <option value='Standard Bank'>Standard Bank</option>
                        <option value='TymeBank'>TymeBank</option>
                        <option value='Other'>Other</option>
                    </SelectInput>
                    {isOtherBank ?
                        <TextInput
                            placeholder='Other Bank Name'
                            value={OtherBankName}
                            onChange={(event) => setOtherBankName(event.target.value)}
                        />
                        : null}
                    <TextInput
                        placeholder='Branch Code'
                        value={BranchCode}
                        onChange={(event) => setBranchCode(event.target.value)}
                        readOnly={!isOtherBank}
                    />
                    <TextInput
                        placeholder='Account Number'
                        value={AccountNumber}
                        onChange={(event) => setAccountNumber(event.target.value)}
                    />
                    <label className='withdrawFileInput' htmlFor='proofOfAccount'>
                        <p>Proof of Account</p>
                        <span>{ProofOfAccount ? ProofOfAccountFileName : 'Upload PDF'}</span>
                        {ProofOfAccount ?
                            <button
                                type='button'
                                className='clearProofOfAccount'
                                aria-label='Clear proof of account'
                                onClick={clearProofOfAccount}
                            >
                                ×
                            </button>
                            : null}
                        <input
                            ref={proofOfAccountInputRef}
                            id='proofOfAccount'
                            type='file'
                            accept='application/pdf,.pdf'
                            required
                            onChange={proofOfAccountChanged}
                        />
                    </label>
                    {error !== '' ?
                        <p style={{fontSize: '15px', color: '#D42E79', fontWeight: '300'}}>
                            {error}
                        </p>
                        : null}
                    <Button enabled={canSubmit}>Request Withdrawal</Button>
                </form>
            </div>
        </View>
    );
});

export default Withdraw;
